import { getLocalTimeZone, today } from '@internationalized/date';
import { BigNumber } from 'bignumber.js';
import { parseISO } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookkeepingConfiguration,
  useBookkeepingConfiguration,
} from '~/services/bookkeeping/bookkeeping-configuration';
import { DocumentDirection } from '~/types/document';
import { FlowWithDocument, PaymentMethod } from '~/types/flow';
import {
  BookkeepingBankTransaction,
  BookkeepingEntrySource,
  BookkeepingTransaction,
  TransactionRegistrationProposal,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { ReconciliationRelationStatus } from '~/types/reconciliation';
import { BookkeepingTransactionTemplateBody } from '~/types/registration-template';
import { Transaction } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { bookkeepingEntryToFormEntry } from '~/utils/bookkeeping/bookkeeping-entry';
import { fromTransactionProposalToFormValues } from '~/utils/bookkeeping/registrations';
import { isNotEmpty } from '~/utils/helpers';
import { ensureDecimalPlaces, isMoreThanZero, maybeSumMoney, newMoney } from '~/utils/money';
import {
  BookkeepingEntryFormInput,
  BookkeepingTransactionFormInput,
} from '~/views/bookkeeping/transactions/BookkeepingTransactionSchema';

const getTransactionWithholdingEntry = (
  direction: DocumentDirection,
  amount: Money,
  bookkeepingConfiguration: BookkeepingConfiguration,
) => {
  if (!isMoreThanZero(amount)) return null;

  const isReceived = direction === DocumentDirection.received;
  const bookkeepingAccountId = isReceived
    ? bookkeepingConfiguration.accounts.withholdingAccount
    : bookkeepingConfiguration.accounts.withholdingAccountSales;
  return {
    id: null,
    counterpart: null,
    bookkeepingAccountId: bookkeepingAccountId ?? '',
    flowId: null,
    documentId: null,
    documentNumber: null,
    documentType: null,
    dare: !isReceived ? amount : null,
    avere: isReceived ? amount : null,
    source: BookkeepingEntrySource.USER,
    competencePeriodStart: null,
    competencePeriodEnd: null,
  };
};

const getFormValuesFromBookkeepingTransaction = (
  bookkeepingTransaction: BookkeepingTransaction,
  recommendedReconciliationFlow: FlowWithDocument | null,
) => {
  return {
    date: parseISO(bookkeepingTransaction.date),
    description: bookkeepingTransaction.description,
    entries: bookkeepingTransaction.bookkeepingEntries.map(bookkeepingEntryToFormEntry),
    recommendedReconciliationFlowId: recommendedReconciliationFlow?.id || null,
  } satisfies BookkeepingTransactionFormInput;
};

const getFormValuesFromRegistrationProposal = (
  registrationProposal: TransactionRegistrationProposal,
  recommendedReconciliationFlow: FlowWithDocument | null,
  bookkeepingConfiguration: BookkeepingConfiguration,
) => {
  const values = fromTransactionProposalToFormValues(registrationProposal);
  const document = recommendedReconciliationFlow?.document;
  const isTaxFormFlow = recommendedReconciliationFlow?.paymentMethod === PaymentMethod.TaxForm;

  if (document?.withholdingTax && !isTaxFormFlow) {
    const withholdingEntry = getTransactionWithholdingEntry(
      document.direction,
      newMoney(document.withholdingTax.amount),
      bookkeepingConfiguration,
    );
    if (withholdingEntry) {
      values.entries.push(withholdingEntry);
    }
  }
  values.entries = values.entries.map((entry) => removeNonEurAmount(entry));
  return values;
};

const getFormValuesFromRegistrationTemplate = (
  templateBody: BookkeepingTransactionTemplateBody,
): BookkeepingTransactionFormInput => ({
  date: today(getLocalTimeZone()).toDate(getLocalTimeZone()),
  description: templateBody.description ?? '',
  entries: templateBody.entries.map((entry) => ({
    id: null,
    bookkeepingAccountId: entry.bookkeepingAccountId ?? '',
    counterpart: entry.counterpartId ? { id: entry.counterpartId, companyName: null } : null,
    description: entry.description ?? '',
    dare: null,
    avere: null,
    flowId: null,
    documentId: null,
    documentNumber: null,
    documentType: null,
    source: BookkeepingEntrySource.USER,
    competencePeriodStart: null,
    competencePeriodEnd: null,
  })),
  recommendedReconciliationFlowId: null,
});

const getBookkeepingAccountIdFromReconciliation = (
  bookkeepingConfiguration: BookkeepingConfiguration,
  flow?: FlowWithDocument,
) => {
  if (!flow?.document) {
    return '';
  }
  if (flow.document.direction === DocumentDirection.issued) {
    return bookkeepingConfiguration.accounts.creditsClients ?? '';
  }
  return bookkeepingConfiguration.accounts.debtsSuppliers ?? '';
};

const useTransactionDescription = () => {
  const { t } = useTranslation('documents');
  return useCallback(
    (transaction: Transaction) => {
      if (transaction.reconciliations && transaction.reconciliations.length > 1) {
        const counterpartName = transaction.reconciliations[0]?.flow?.document.counterpart?.companyName;
        const documentType = transaction.reconciliations[0]?.flow?.document.documentType;
        const documentNumber = transaction.reconciliations[0]?.flow?.document.number;
        if (counterpartName && documentType && documentNumber) {
          const typeString = t(`type.${documentType}`);
          return `${counterpartName} — ${typeString} ${documentNumber} `;
        }
      }
      return transaction.description;
    },
    [t],
  );
};

const getEntriesFromTransaction = (
  transaction: Transaction | null,
  recommendedReconciliationFlow: FlowWithDocument | null | undefined,
  bookkeepingConfiguration: BookkeepingConfiguration,
): BookkeepingEntryFormInput[] | null => {
  if (!transaction) {
    return null;
  }

  const reconciliations = transaction.reconciliations ?? [];
  const amount = BigNumber(transaction.amount.amount);
  const money = ensureDecimalPlaces({ amount: amount.abs().toFixed(), currency: transaction.amount.currency }, 2);
  const entries: BookkeepingEntryFormInput[] = [];

  entries.push({
    id: null,
    bookkeepingAccountId: transaction.account.bookkeepingAccount?.id ?? '',
    counterpart: null,
    dare: amount.isPositive() ? ensureDecimalPlaces(money, 2) : null,
    avere: amount.isNegative() ? ensureDecimalPlaces(money, 2) : null,
    readOnlyAccount: isDefined(transaction.account.bookkeepingAccount?.id),
    readOnlyAmount: true,
    flowId: null,
    documentId: null,
    documentNumber: null,
    documentType: null,
    source: BookkeepingEntrySource.USER,
    competencePeriodStart: null,
    competencePeriodEnd: null,
  });

  if (transaction.calculatedBankFees) {
    entries.push({
      id: null,
      bookkeepingAccountId: bookkeepingConfiguration.accounts.bankFees ?? '',
      counterpart: null,
      dare: ensureDecimalPlaces(transaction.calculatedBankFees, 2),
      avere: null,
      flowId: null,
      documentId: null,
      documentNumber: null,
      documentType: null,
      source: BookkeepingEntrySource.USER,
      competencePeriodStart: null,
      competencePeriodEnd: null,
    });
  }

  const getEntryFromFlow = (flow?: FlowWithDocument) => {
    if (flow?.paymentMethod === PaymentMethod.TaxForm) {
      const isReceived = flow.document.direction === DocumentDirection.received;
      const bookkeepingAccountId =
        (isReceived
          ? bookkeepingConfiguration.accounts.withholdingAccount
          : bookkeepingConfiguration.accounts.withholdingAccountSales) ?? '';

      return {
        id: null,
        bookkeepingAccountId,
        counterpart: null,
        flowId: null,
        documentId: null,
        documentNumber: null,
        documentType: null,
        dare: amount.isNegative() ? ensureDecimalPlaces(flow.amount, 2) : null,
        avere: amount.isPositive() ? ensureDecimalPlaces(flow.amount, 2) : null,
        source: BookkeepingEntrySource.RECONCILIATION,
        competencePeriodStart: null,
        competencePeriodEnd: null,
      };
    }

    const withholdingTax = flow?.document.withholdingTax;
    const reconciliationAmount = withholdingTax ? maybeSumMoney(flow.amount, withholdingTax) : flow!.amount;

    return {
      id: null,
      bookkeepingAccountId: getBookkeepingAccountIdFromReconciliation(bookkeepingConfiguration, flow),
      counterpart: flow?.document.counterpart
        ? {
            id: flow.document.counterpart.id,
            companyName: flow.document.counterpart.companyName,
          }
        : null,
      flowId: flow?.id ?? null,
      documentId: flow?.document.id ?? null,
      documentNumber: flow?.document.number ?? null,
      documentType: flow?.document.documentType ?? null,
      dare: amount.isNegative() && reconciliationAmount ? ensureDecimalPlaces(reconciliationAmount, 2) : null,
      avere: amount.isPositive() && reconciliationAmount ? ensureDecimalPlaces(reconciliationAmount, 2) : null,
      source: BookkeepingEntrySource.RECONCILIATION,
      competencePeriodStart: null,
      competencePeriodEnd: null,
    };
  };

  const getWithholdingEntry = () => {
    const receivedWithholdingTaxes: Money | null = reconciliations
      .filter(
        (rec) =>
          rec.flow?.document.direction === DocumentDirection.received &&
          rec.flow.paymentMethod !== PaymentMethod.TaxForm,
      )
      .map((rec) => rec.flow?.document.withholdingTax ?? newMoney(0))
      .reduce<Money | null>((acc, curr) => acc && maybeSumMoney(acc, curr), newMoney(0));

    if (receivedWithholdingTaxes && isMoreThanZero(receivedWithholdingTaxes)) {
      return getTransactionWithholdingEntry(
        DocumentDirection.received,
        receivedWithholdingTaxes,
        bookkeepingConfiguration,
      );
    }

    const issuedWithholdingTaxes: Money | null = reconciliations
      .filter(
        (rec) =>
          rec.flow?.document.direction === DocumentDirection.issued && rec.flow.paymentMethod !== PaymentMethod.TaxForm,
      )
      .map((rec) => rec.flow?.document.withholdingTax ?? newMoney(0))
      .reduce<Money | null>((acc, curr) => acc && maybeSumMoney(acc, curr), newMoney(0));

    if (issuedWithholdingTaxes && isMoreThanZero(issuedWithholdingTaxes)) {
      return getTransactionWithholdingEntry(DocumentDirection.issued, issuedWithholdingTaxes, bookkeepingConfiguration);
    }

    return null;
  };

  switch (true) {
    case isNotEmpty(reconciliations):
      entries.push(
        ...reconciliations
          .filter((reconciliation) => reconciliation.status === ReconciliationRelationStatus.VERIFIED)
          .map((reconciliation) => getEntryFromFlow(reconciliation.flow)),
      );
      break;

    case isDefined(recommendedReconciliationFlow):
      entries.push(getEntryFromFlow(recommendedReconciliationFlow));
      break;

    default:
      entries.push({
        id: null,
        bookkeepingAccountId: '',
        counterpart: null,
        dare: amount.isNegative() ? ensureDecimalPlaces(money, 2) : null,
        avere: amount.isPositive() ? ensureDecimalPlaces(money, 2) : null,
        flowId: null,
        documentId: null,
        documentNumber: null,
        documentType: null,
        source: BookkeepingEntrySource.USER,
        competencePeriodStart: null,
        competencePeriodEnd: null,
      });
      break;
  }

  const withholdingEntry = getWithholdingEntry();
  if (withholdingEntry) {
    entries.push(withholdingEntry);
  }

  return entries.map((entry) => removeNonEurAmount(entry));
};

const removeNonEurAmount = (entry: BookkeepingEntryFormInput): BookkeepingEntryFormInput => {
  const currency = entry.avere?.currency || entry.dare?.currency;
  if (!currency || currency === 'EUR') {
    return entry;
  }

  return { ...entry, dare: null, avere: null };
};

export const useBookkeepingTransactionFormDefaultValues = ({
  transaction,
  bookkeepingTransaction,
  recommendedReconciliationFlow,
  proposal = null,
  templateBody = null,
}: {
  transaction: BookkeepingBankTransaction | null;
  bookkeepingTransaction: BookkeepingTransaction | null;
  recommendedReconciliationFlow: FlowWithDocument | null;
  proposal?: TransactionRegistrationProposal | null;
  templateBody?: BookkeepingTransactionTemplateBody | null;
}): BookkeepingTransactionFormInput => {
  const transactionDescription = useTransactionDescription();
  const bookkeepingConfiguration = useBookkeepingConfiguration();

  if (isDefined(bookkeepingTransaction)) {
    // If there's an existing bookkeeping transaction, use its values
    // used in editing existing bookkeeping transaction
    return getFormValuesFromBookkeepingTransaction(bookkeepingTransaction, recommendedReconciliationFlow);
  }

  if (isDefined(transaction?.registrationProposal)) {
    // If there's a registration proposal, use its values
    return getFormValuesFromRegistrationProposal(
      transaction.registrationProposal,
      recommendedReconciliationFlow,
      bookkeepingConfiguration,
    );
  }

  if (isDefined(proposal)) {
    // A standalone registration proposal not tied to a bank transaction.
    // The entries already carry their amounts, so we map them straight to form values.
    return fromTransactionProposalToFormValues(proposal);
  }

  if (isDefined(templateBody)) {
    return getFormValuesFromRegistrationTemplate(templateBody);
  }

  // Default entries for new bookkeeping transaction
  const entries: BookkeepingEntryFormInput[] = getEntriesFromTransaction(
    transaction,
    recommendedReconciliationFlow,
    bookkeepingConfiguration,
  ) || [
    {
      id: null,
      bookkeepingAccountId: '',
      counterpart: null,
      description: '',
      dare: null,
      avere: null,
      flowId: null,
      documentId: null,
      documentNumber: null,
      documentType: null,
      source: BookkeepingEntrySource.USER,
      competencePeriodStart: null,
      competencePeriodEnd: null,
    },
    {
      id: null,
      bookkeepingAccountId: '',
      counterpart: null,
      description: '',
      dare: null,
      avere: null,
      flowId: null,
      documentId: null,
      documentNumber: null,
      documentType: null,
      source: BookkeepingEntrySource.USER,
      competencePeriodStart: null,
      competencePeriodEnd: null,
    },
  ];

  return {
    date: transaction?.date ? parseISO(transaction.date) : today(getLocalTimeZone()).toDate(getLocalTimeZone()),
    description: transaction ? transactionDescription(transaction) : '',
    entries,
    recommendedReconciliationFlowId: recommendedReconciliationFlow?.id || null,
  } satisfies BookkeepingTransactionFormInput;
};
